/**
 * ユーザー管理画面の表示ラベルと集計 (ユーザー管理リデザイン)
 *
 * すべて取得済みの `AdminUser[]` から導出する純関数で、API・型・権限判定は一切変えない。
 *
 * **「招待中」という状態は作らない (Mgr 判断 D1)。** BE の user は `active | disabled` の
 * 2 値しか持たず、作成直後も `active` + `lastLoginAt = null` になる。したがって
 * 「まだ一度もログインしていない」を **派生値** として出すに留め、
 * ステータス列と絞り込みの意味 (有効 / 無効) は従来どおり保つ。
 */
import type { AdminUser, BadgeVariant, UserRole } from '@/types';

/** ロールの表示ラベル。 */
export const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
  bank_operator: '銀行担当',
  superadmin: 'Super Admin',
};

/** ロールのバッジ色。 */
export const ROLE_BADGE_VARIANT: Record<UserRole, BadgeVariant> = {
  owner: 'red',
  admin: 'blue',
  member: 'green',
  viewer: 'gray',
  bank_operator: 'cyan',
  superadmin: 'purple',
};

/** 職種の表示ラベル。未設定・未知の値は「-」。 */
export const POSITION_LABELS: Record<string, string> = {
  lawyer: '弁護士',
  staff: '事務員',
  paralegal: 'パラリーガル',
  other: 'その他',
};

/** 職種ラベル。未設定 / 未知の値でも落ちない (未マップ値の fallback を必ず持つ)。 */
export function positionLabel(position: string | null | undefined): string {
  if (!position) return '-';
  return POSITION_LABELS[position] ?? '-';
}

/** 職種の絞り込み選択肢 (表示順)。 */
export const POSITION_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'lawyer', label: '弁護士' },
  { value: 'staff', label: '事務員' },
  { value: 'paralegal', label: 'パラリーガル' },
  { value: 'other', label: 'その他' },
];

/** ステータスの表示ラベル。値は BE の 2 値のまま。 */
export function statusLabel(status: AdminUser['status']): string {
  return status === 'active' ? '有効' : '無効';
}

/** ステータスのバッジ色。 */
export function statusBadgeVariant(status: AdminUser['status']): BadgeVariant {
  return status === 'active' ? 'green' : 'gray';
}

/**
 * 「まだ一度もログインしていない」か (Mgr 判断 D1)。
 *
 * 無効化済みユーザーは対象にしない — 見たいのは「招待したが使い始めていない人」で、
 * 止めた人ではないため。**ステータスそのものを置き換えるものではない**。
 */
export function isNeverLoggedIn(user: Pick<AdminUser, 'status' | 'lastLoginAt'>): boolean {
  return user.status === 'active' && !user.lastLoginAt;
}

/**
 * ログイン状況の絞り込み値 (ユーザー管理フィルター UI 改善)。`''` = すべて。
 *
 * サマリーカードの「未ログイン」を絞り込み軸としても使えるようにするために追加した。
 * **意味は既存の `isNeverLoggedIn` を唯一の正本とし、ここで新しい定義を作らない。**
 */
export type LoginStatusFilter = '' | 'logged_in' | 'never';

/** ログイン状況の絞り込み選択肢 (表示順)。`''` (すべて) は select 側で持つ。 */
export const LOGIN_STATUS_FILTER_OPTIONS: {
  value: Exclude<LoginStatusFilter, ''>;
  label: string;
}[] = [
  { value: 'logged_in', label: 'ログイン済み' },
  { value: 'never', label: '未ログイン' },
];

/**
 * ログイン状況で 1 件を判定する。
 *
 * - `never` … サマリーカード「未ログイン」と **同じ母集団** (`isNeverLoggedIn`)。
 *   判定を共有することで、カードの件数と絞り込み結果の件数が食い違わない。
 * - `logged_in` … 一度でもログインした人 (最終ログインあり)。無効化済みでも該当する。
 *
 * 無効化済みかつ未ログインの人はどちらにも入らず「すべて」でのみ見える。これは
 * 「未ログイン = 招待したが使い始めていない人 (止めた人は含めない)」という既存定義
 * (Mgr 判断 D1) を保った帰結で、絞り込みのためにその意味を書き換えない。
 */
export function matchesLoginStatus(
  user: Pick<AdminUser, 'status' | 'lastLoginAt'>,
  filter: LoginStatusFilter,
): boolean {
  if (!filter) return true;
  if (filter === 'never') return isNeverLoggedIn(user);
  return !!user.lastLoginAt;
}

/** サマリーカードの集計結果。 */
export interface AdminUserSummary {
  total: number;
  owner: number;
  admin: number;
  member: number;
  viewer: number;
  neverLoggedIn: number;
}

/**
 * サマリー集計。**絞り込み前の全件**を渡すこと (カードは組織全体の内訳を示すため)。
 * 銀行担当 / Super Admin は総数に含まれるが専用カードは持たない (正本の 6 枚構成に合わせる)。
 */
export function summarizeAdminUsers(users: AdminUser[]): AdminUserSummary {
  const summary: AdminUserSummary = {
    total: 0,
    owner: 0,
    admin: 0,
    member: 0,
    viewer: 0,
    neverLoggedIn: 0,
  };
  for (const u of users) {
    // 無効化したユーザーはどのカードにも数えない (成山さん 2026-09-08: 「有効化しているメンバーのみ集計」)。
    // 一覧の既定 (ステータス = 有効) と件数が一致する。
    if (u.status !== 'active') continue;
    summary.total += 1;
    if (u.role === 'owner') summary.owner += 1;
    else if (u.role === 'admin') summary.admin += 1;
    else if (u.role === 'member') summary.member += 1;
    else if (u.role === 'viewer') summary.viewer += 1;
    if (isNeverLoggedIn(u)) summary.neverLoggedIn += 1;
  }
  return summary;
}

/**
 * アバターの地色。表示名から決定的に選ぶ (同じ人はいつも同じ色)。
 * 実在しない顔写真は出さない — 頭文字だけの円を描く。
 */
const AVATAR_TONES = [
  'bg-indigo-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-amber-500',
] as const;

/**
 * seed 欠落を許容する。BE の `listUsers` は DB レコードをそのまま返すため、
 * 2026-05-08 以前に一括招待された legacy USER 行は `userId` を持たない。
 * 装飾 (aria-hidden) のために画面全体を落とさないよう既定 tone へ倒す。
 */
export function avatarToneFor(seed: string | null | undefined): string {
  const normalized = seed ?? '';
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash * 31 + normalized.charCodeAt(i)) % 100000;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

/** アバターに出す 1 文字。表示名が空なら空文字 (記号で代用しない)。 */
export function avatarInitial(name: string): string {
  const trimmed = (name || '').trim();
  return trimmed ? trimmed.slice(0, 1) : '';
}

/** 件数/ページの選択肢 (Mgr 判断 D4)。既定は 20 件で従来挙動を維持する。 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
export const DEFAULT_PAGE_SIZE = 20;

/** 総ページ数。0 件でも 1 ページ扱い (空ページを出さないため)。 */
export function totalPageCount(totalCount: number, pageSize: number): number {
  return Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
}

/**
 * ページ番号を有効範囲へ丸める (Mgr 判断 D4)。
 * 絞り込みや件数/ページの変更で件数が減ったとき、0 件のページに取り残されないようにする。
 */
export function clampPage(page: number, totalCount: number, pageSize: number): number {
  const last = totalPageCount(totalCount, pageSize);
  if (!Number.isFinite(page) || page < 1) return 1;
  return Math.min(Math.trunc(page), last);
}

/** 「全 N 件中 x-y 件を表示」の x と y。0 件のときは 0-0。 */
export function pageRange(
  page: number,
  totalCount: number,
  pageSize: number,
): { from: number; to: number } {
  if (totalCount === 0) return { from: 0, to: 0 };
  const safePage = clampPage(page, totalCount, pageSize);
  const from = (safePage - 1) * pageSize + 1;
  return { from, to: Math.min(safePage * pageSize, totalCount) };
}

/**
 * ページャに出す番号列。両端と現在ページの周辺だけを残し、飛ぶ箇所に省略記号を挟む。
 * 旧実装と同じ見え方を保ったまま、ロジックだけ純関数として切り出した。
 */
export function paginationItems(page: number, totalPages: number): (number | 'ellipsis')[] {
  return Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push('ellipsis');
      acc.push(p);
      return acc;
    }, []);
}
